import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { difficulty, scaleScore } from '@src/core/Difficulty';
import { GameEvent } from '@src/core/EventQueue';
import { enemyScore, kill, playClip, randomPrizeKind, spawnBonusPickup, spawnFx, spawnGem, spawnHeart, spawnPrize, spawnSlime, spawnWeaponPickup, startDeath } from '@src/ecs/archetypes';
import { Anim, BossTag, ClipId, Dying, Enemy, EnemyKind, Health, Lifetime, Player, Position, Projectile, Radius, Spawner, Sprite, SpriteId, Team, TeamTag, Velocity } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { BossState, emitParticles, emitPopup, ParticleKind, PopupKind, requestDecal, RoomPhase, type GameContext } from '@src/ecs/world';
import { Anims } from '@src/game/AnimDefs';
import { bossDef, bossTuning } from '@src/game/BossDefs';
import { dealDamage } from '@src/game/Combat';
import { deathHoldSeconds, emitBossKillGore, emitKillGore, pickDeathVariant } from '@src/game/DeathFx';
import { floorMod } from '@src/game/FloorModifiers';
import { rollWeaponDrop } from '@src/game/weapons/WeaponDefs';

const PORTAL_BURN = 0x6a1fb0;
const CINDER_SCORCH = 0x2a1208;
const CINDER_FIRE = 0xff8a30;
const BLAST_CANDIDATES = 96;
/** Kills within this window chain into a combo. */
const COMBO_WINDOW = 1.5;
const STREAK_KILLS = 20;
const STREAK_WINDOW = 4;

/**
 * Resolves deaths after collision: score, combo, drops, decals, death clips,
 * events. Also ticks invulnerability timers.
 */
export class DamageSystem implements GameSystem {
  private ctx!: GameContext;
  private readonly blastCandidates = new Int32Array(BLAST_CANDIDATES);
  private readonly streakTimes = new Float64Array(STREAK_KILLS);
  private streakHead = 0;
  private lastStreak = -Infinity;

  init(ctx: GameContext): void {
    this.ctx = ctx;
    this.streakHead = 0;
    this.lastStreak = -Infinity;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;

    const healthy = query(ctx.world, [Health]);
    for (let i = 0; i < healthy.length; i++) {
      const eid = healthy[i];
      if (Health.invuln[eid] > 0) Health.invuln[eid] -= dt;
    }

    if (ctx.combo.count > 0 && ctx.time - ctx.combo.lastKill > COMBO_WINDOW) {
      ctx.combo.count = 0;
      ctx.combo.multiplier = 1;
    }

    const enemies = query(ctx.world, [Enemy, Health, Position]);
    for (let i = 0; i < enemies.length; i++) {
      const eid = enemies[i];
      if (Dying.flag[eid] || Health.hp[eid] > 0) continue;
      this.onEnemyDeath(eid);
    }

    const spawners = query(ctx.world, [Spawner, Health, Position]);
    for (let i = 0; i < spawners.length; i++) {
      const eid = spawners[i];
      if (Dying.flag[eid] || Health.hp[eid] > 0) continue;
      this.onSpawnerDeath(eid);
    }

    const players = query(ctx.world, [Player, Health, Position]);
    for (let i = 0; i < players.length; i++) {
      const eid = players[i];
      if (Player.dead[eid]) {
        // Once the death clip has played out, the wizard lingers as a ghost.
        if (Sprite.id[eid] === SpriteId.Player && Anim.done[eid]) {
          Sprite.id[eid] = SpriteId.Ghost;
          Sprite.alpha[eid] = 0.6;
          playClip(eid, ClipId.Idle, true);
        }
        continue;
      }
      if (Health.hp[eid] > 0) continue;
      this.onPlayerDeath(eid);
    }
  }

  private bumpCombo(): number {
    const ctx = this.ctx;
    const combo = ctx.combo;
    combo.count++;
    combo.lastKill = ctx.time;
    combo.multiplier = 1 + Math.min(9, Math.floor(combo.count / 5));
    if (combo.count > combo.best) combo.best = combo.count;

    // TOTAL MAYHEM: STREAK_KILLS kills inside STREAK_WINDOW seconds, once per burst.
    const slot = this.streakHead % STREAK_KILLS;
    this.streakTimes[slot] = ctx.time;
    this.streakHead++;
    if (this.streakHead >= STREAK_KILLS) {
      const oldest = this.streakTimes[this.streakHead % STREAK_KILLS];
      if (ctx.time - oldest <= STREAK_WINDOW && ctx.time - this.lastStreak > STREAK_WINDOW * 2) {
        this.lastStreak = ctx.time;
        ctx.events.push(GameEvent.KillStreak, combo.count, 0, 0, 0);
      }
    }
    return combo.multiplier;
  }

  private onEnemyDeath(eid: number): void {
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    const kind = Enemy.kind[eid] as EnemyKind;

    if (kind === EnemyKind.Boss) {
      this.onBossDeath(eid);
      return;
    }

    const mult = this.bumpCombo();
    const points = scaleScore(enemyScore(kind) * ctx.room.tier * mult);
    ctx.score += points;
    const heartDrop = difficulty().heartDrop;
    const bonusGem = floorMod(ctx.floor.modifier).bonusGemChance;
    ctx.kills++;
    ctx.events.push(GameEvent.EnemyDied, eid, kind, x, y);
    const vx = Velocity.x[eid];
    const vy = Velocity.y[eid];
    const speed = Math.hypot(vx, vy);
    ctx.fx.shakeX += (vx / (speed || 1)) * Config.fx.killShake;
    ctx.fx.shakeY += (vy / (speed || 1)) * Config.fx.killShake;

    const variant = pickDeathVariant(ctx.rng);
    Enemy.mode[eid] = variant;
    emitKillGore(ctx, kind, x, y, vx, vy, variant);
    if (kind === EnemyKind.Cinder) this.cinderBlast(eid, x, y);

    switch (kind) {
      case EnemyKind.Golem: {
        emitPopup(ctx.fx, PopupKind.Score, points, x, y + 0.8);
        if (ctx.rng.chance(Config.enemies.golem.heartChance * heartDrop)) {
          spawnHeart(ctx, x, y);
        } else if (ctx.rng.chance(Config.enemies.golem.prizeChance)) {
          spawnPrize(ctx, x, y, randomPrizeKind(ctx.rng));
        } else {
          for (let g = 0; g < 3; g++) spawnGem(ctx, x, y, 3);
        }
        break;
      }
      case EnemyKind.Slime: {
        emitPopup(ctx.fx, PopupKind.Score, points, x, y + 0.5);
        if (ctx.rng.chance(Config.enemies.slime.heartChance * heartDrop)) spawnHeart(ctx, x, y);
        else if (ctx.rng.chance(0.3)) spawnGem(ctx, x, y, 2);
        break;
      }
      case EnemyKind.Gargoyle: {
        emitPopup(ctx.fx, PopupKind.Score, points, x, y + 0.7);
        if (ctx.rng.chance(Config.enemies.gargoyle.gemChance)) spawnGem(ctx, x, y, 2);
        break;
      }
      case EnemyKind.Spitter: {
        emitPopup(ctx.fx, PopupKind.Score, points, x, y + 0.7);
        if (ctx.rng.chance(Config.enemies.spitter.gemChance)) spawnGem(ctx, x, y, 2);
        break;
      }
      case EnemyKind.Hexer: {
        if (mult > 1 || ctx.rng.chance(0.4)) emitPopup(ctx.fx, PopupKind.Score, points, x, y + 0.5);
        if (ctx.rng.chance(Config.pickups.impHeartChance * heartDrop)) spawnHeart(ctx, x, y);
        else if (ctx.rng.chance(Config.enemies.hexer.gemChance)) spawnGem(ctx, x, y, 1);
        break;
      }
      case EnemyKind.Blight: {
        emitPopup(ctx.fx, PopupKind.Score, points, x, y + 0.6);
        if (ctx.room.phase === RoomPhase.Fight) this.blightSplit(x, y);
        if (ctx.rng.chance(Config.enemies.blight.heartChance * heartDrop)) spawnHeart(ctx, x, y);
        else if (ctx.rng.chance(0.4)) spawnGem(ctx, x, y, 2);
        break;
      }
      case EnemyKind.Brute: {
        emitPopup(ctx.fx, PopupKind.Score, points, x, y + 0.8);
        if (ctx.rng.chance(Config.enemies.brute.heartChance * heartDrop)) {
          spawnHeart(ctx, x, y);
        } else if (ctx.rng.chance(Config.enemies.brute.prizeChance)) {
          spawnPrize(ctx, x, y, randomPrizeKind(ctx.rng));
        } else {
          for (let g = 0; g < 3; g++) spawnGem(ctx, x, y, 3);
        }
        break;
      }
      case EnemyKind.Mimic: {
        const c = Config.enemies.mimic;
        emitPopup(ctx.fx, PopupKind.Score, points, x, y + 0.8);
        for (let g = 0; g < c.gems; g++) spawnGem(ctx, x, y, 3);
        if (ctx.rng.chance(c.prizeChance)) spawnPrize(ctx, x, y, randomPrizeKind(ctx.rng));
        ctx.events.push(GameEvent.MimicSlain, eid, 0, x, y);
        break;
      }
      case EnemyKind.Imp: {
        if (mult > 1 || ctx.rng.chance(0.25)) emitPopup(ctx.fx, PopupKind.Score, points, x, y + 0.5);
        if (ctx.rng.chance(Config.pickups.impHeartChance * heartDrop)) spawnHeart(ctx, x, y);
        else if (ctx.rng.chance(Config.enemies.imp.gemChance)) spawnGem(ctx, x, y, 1);
        break;
      }
      default: {
        if (mult > 1 || ctx.rng.chance(0.3)) emitPopup(ctx.fx, PopupKind.Score, points, x, y + 0.5);
        if (kind === EnemyKind.Bat && ctx.rng.chance(0.12)) spawnGem(ctx, x, y, 1);
        else if (kind === EnemyKind.Wraith && ctx.rng.chance(0.15)) spawnGem(ctx, x, y, 1);
        break;
      }
    }
    if (bonusGem > 0 && ctx.rng.chance(bonusGem)) spawnGem(ctx, x, y, 1);
    if (ctx.combo.count > 0 && ctx.combo.count % 5 === 0) emitPopup(ctx.fx, PopupKind.Combo, ctx.combo.count, x, y + 1.1);
    startDeath(ctx, eid);
    Lifetime.t[eid] = Math.max(Lifetime.t[eid], deathHoldSeconds(kind));
  }

  /**
   * A cinder going out is the point of a cinder: scorch the floor, burn the wizard if
   * they are close, and cook every other fiend in the ring (which chains cinders).
   */
  private cinderBlast(eid: number, x: number, y: number): void {
    const ctx = this.ctx;
    const c = Config.enemies.cinder;
    const r = c.blastRadius;
    ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, Config.fx.hitStopMs * 0.8);
    ctx.fx.shakeX += (ctx.rng.next() - 0.5) * 0.9;
    ctx.fx.shakeY += (ctx.rng.next() - 0.5) * 0.9;
    ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.7);
    ctx.fx.flash = Math.max(ctx.fx.flash, 0.18);
    requestDecal(ctx.fx, x, y, CINDER_SCORCH, r * 0.9);
    spawnFx(ctx, SpriteId.Explosion, x, y, ctx.rng.next() * Math.PI * 2, r * 1.5, CINDER_FIRE, 0.7);
    spawnFx(ctx, SpriteId.Shockwave, x, y, 0, r * 0.9, CINDER_FIRE, 0.5);
    emitParticles(ctx.fx, ParticleKind.Embers, x, y, 0, 0, 1.6, CINDER_FIRE);
    emitParticles(ctx.fx, ParticleKind.HitSpark, x, y, 0, 0, 1.0, 0xffd080);
    ctx.events.push(GameEvent.CinderBlast, eid, 0, x, y);

    const players = query(ctx.world, [Player, Position, Radius, Health]);
    for (let i = 0; i < players.length; i++) {
      const pid = players[i];
      if (Player.dead[pid]) continue;
      const dx = Position.x[pid] - x;
      const dy = Position.y[pid] - y;
      const reach = r + Radius.r[pid];
      const d2 = dx * dx + dy * dy;
      if (d2 > reach * reach) continue;
      const d = Math.sqrt(d2) || 1;
      dealDamage(ctx, pid, c.blastDamage, (dx / d) * c.blastKnockback, (dy / d) * c.blastKnockback, 1, 0);
    }

    const n = ctx.grid.gather(x, y, r + 0.9, this.blastCandidates);
    for (let k = 0; k < n; k++) {
      const e = this.blastCandidates[k];
      if (e === eid || Dying.flag[e] || Health.hp[e] <= 0 || Enemy.kind[e] === EnemyKind.Boss) continue;
      const dx = Position.x[e] - x;
      const dy = Position.y[e] - y;
      const reach = r + Radius.r[e];
      const d2 = dx * dx + dy * dy;
      if (d2 > reach * reach) continue;
      const d = Math.sqrt(d2) || 1;
      dealDamage(ctx, e, c.blastFriendly, (dx / d) * c.blastKnockback * 0.6, (dy / d) * c.blastKnockback * 0.6, 1, 0);
    }
  }

  /** Blight death: a couple of ordinary slimes squirt out sideways, tinted like their parent. */
  private blightSplit(x: number, y: number): void {
    const ctx = this.ctx;
    const c = Config.enemies.blight;
    const base = ctx.rng.next() * Math.PI * 2;
    for (let i = 0; i < c.splitCount; i++) {
      const a = base + (i / c.splitCount) * Math.PI * 2;
      const sx = x + Math.cos(a) * 0.5;
      const sy = y + Math.sin(a) * 0.5;
      const eid = spawnSlime(ctx, sx, sy);
      if (eid < 0) return;
      Sprite.tint[eid] = 0xb46cff;
      Sprite.pop[eid] = 1.3;
      Velocity.x[eid] = Math.cos(a) * 5;
      Velocity.y[eid] = Math.sin(a) * 5;
    }
  }

  /**
   * The headliner goes down. Score without combo (it is one kill, not mayhem),
   * a long death clip that BossSystem's crescendo plays over, and the drops
   * land once the body is gone (see BossSystem) so they are not lost in the flash.
   */
  private onBossDeath(eid: number): void {
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    const room = ctx.room;
    const kind = BossTag.kind[eid];
    const tuning = bossTuning(kind, ctx.floor.floor, BossTag.room[eid] || ctx.room.roomIndex, BossTag.hpMult[eid] || 1);
    const points = scaleScore(tuning.score * (bossDef(kind).archmage ? Math.max(1, ctx.floor.floor) : 1));
    ctx.score += points;
    ctx.kills++;
    room.bossState = BossState.Dead;
    ctx.events.push(GameEvent.EnemyDied, eid, EnemyKind.Boss, x, y);
    ctx.events.push(GameEvent.BossDeath, eid, kind, x, y);

    // Every stray shot dies with the caster.
    const projectiles = query(ctx.world, [Projectile, TeamTag]);
    for (let i = 0; i < projectiles.length; i++) {
      if (TeamTag.team[projectiles[i]] === Team.Enemy) kill(projectiles[i]);
    }

    // Slow-mo hitstop: the whole arena freezes while the first crack of light escapes.
    ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 300);
    ctx.fx.shakeX += (ctx.rng.next() - 0.5) * 2.6;
    ctx.fx.shakeY += (ctx.rng.next() - 0.5) * 2.6;
    ctx.fx.rumble = 1;
    ctx.fx.flash = Math.max(ctx.fx.flash, 0.6);
    emitPopup(ctx.fx, PopupKind.Score, points, x, y + 2.2);
    emitParticles(ctx.fx, ParticleKind.HitSpark, x, y, 0, 0, 2.5, bossDef(kind).glowTint);
    emitBossKillGore(ctx, bossDef(kind).body, x, y, 1.8);

    startDeath(ctx, eid);
    Velocity.x[eid] = 0;
    Velocity.y[eid] = 0;
    BossTag.phase[eid] = 2;
    BossTag.phaseTime[eid] = 0;
    BossTag.burstAt[eid] = 0;
    // Stretch the death clip across the whole crescendo.
    const clipSeconds = Anims.duration(Sprite.id[eid] as SpriteId, ClipId.Death);
    Anim.speed[eid] = clipSeconds / tuning.deathSeconds;
    Lifetime.t[eid] = tuning.deathSeconds;
  }

  private onSpawnerDeath(eid: number): void {
    const ctx = this.ctx;
    const x = Position.x[eid];
    const y = Position.y[eid];
    const points = scaleScore(Config.enemies.spawner.score * ctx.room.tier);
    ctx.score += points;
    ctx.room.spawnersAlive = Math.max(0, ctx.room.spawnersAlive - 1);
    ctx.events.push(GameEvent.SpawnerDestroyed, eid, ctx.room.spawnersAlive, x, y);
    ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, Config.fx.hitStopMs * 1.7);
    ctx.fx.shakeX += (ctx.rng.next() - 0.5) * 1.6;
    ctx.fx.shakeY += (ctx.rng.next() - 0.5) * 1.6;
    ctx.fx.rumble = 1;
    ctx.fx.flash = Math.max(ctx.fx.flash, 0.7);
    requestDecal(ctx.fx, x, y, PORTAL_BURN, 3.2);
    spawnFx(ctx, SpriteId.Explosion, x, y, ctx.rng.next() * Math.PI * 2, 4.2, 0xffffff, 0.85);
    spawnFx(ctx, SpriteId.Shockwave, x, y, 0, 1.5, 0xd9a6ff, 0.84);
    emitParticles(ctx.fx, ParticleKind.PortalBurst, x, y, 0, 0, 2.5);
    emitParticles(ctx.fx, ParticleKind.PortalMotes, x, y, 0, 0, 1.8);
    Sprite.squash[eid] = Math.max(Sprite.squash[eid], 0.7);
    emitPopup(ctx.fx, PopupKind.Score, points, x, y + 1.4);

    spawnWeaponPickup(ctx, x, y, rollWeaponDrop(ctx.rng, Config.pickups.cataclysmSpawnerChance));
    if (ctx.rng.chance(Config.pickups.bonusPortalChance)) spawnBonusPickup(ctx, x + 0.7, y);
    for (let g = 0; g < 5; g++) spawnGem(ctx, x, y, 2);
    startDeath(ctx, eid);
  }

  private onPlayerDeath(eid: number): void {
    const ctx = this.ctx;
    Player.dead[eid] = 1;
    Health.hp[eid] = 0;
    Velocity.x[eid] = 0;
    Velocity.y[eid] = 0;
    playClip(eid, ClipId.Death, true, true);
    emitParticles(ctx.fx, ParticleKind.HitSpark, Position.x[eid], Position.y[eid], 0, 0, 2);
    ctx.events.push(GameEvent.PlayerDied, eid, 0, Position.x[eid], Position.y[eid]);
    ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 220);
    ctx.fx.rumble = 1;
  }
}
